import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface ClienteCardProps {
  cliente: {
    id: string;
    razon_social: string;
    nombre_comercial?: string;
    docs_pendientes: number;
    docs_procesados_ia: number;
    ultimo_documento?: string;
    ultimo_estado?: string;
  };
  onTrabajar: (clienteId: string) => void;
  isLoading?: boolean;
}

export function ClienteCard({ cliente, onTrabajar, isLoading }: ClienteCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Gerar cor consistente baseada no nome
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-indigo-500',
      'bg-rose-500',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Iniciais do nome
  const getIniciais = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Formatar tempo relativo
  const getTiempoRelativo = (fecha?: string) => {
    if (!fecha) return 'Sin documentos';
    const date = new Date(fecha);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours} h`;
    if (diffDays === 1) return 'ayer';
    return `hace ${diffDays} días`;
  };

  // Cor do badge baseada en cantidad
  const getBadgeClasses = (count: number) => {
    if (count > 10) return 'bg-red-100 text-red-800 border-red-300';
    if (count > 5) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  const totalDocs = cliente.docs_pendientes + cliente.docs_procesados_ia;

  return (
    <Card
      className={cn(
        'h-full transition-all duration-200 cursor-pointer',
        'hover:shadow-lg hover:border-blue-300',
        isHovered && 'shadow-md border-blue-400'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-5 space-y-4">
        {/* Header con avatar y nombre */}
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0',
              getAvatarColor(cliente.razon_social)
            )}
          >
            {getIniciais(cliente.razon_social)}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate text-base leading-tight">
              {cliente.razon_social}
            </h3>
            {cliente.nombre_comercial && (
              <p className="text-sm text-gray-500 truncate">{cliente.nombre_comercial}</p>
            )}
          </div>
        </div>

        {/* Contador de documentos */}
        <div className="flex items-center gap-2">
          <Badge
            className={cn(
              'text-sm px-3 py-1 font-medium border',
              getBadgeClasses(totalDocs),
              totalDocs > 0 && 'animate-pulse'
            )}
          >
            {totalDocs} a contabilizar
          </Badge>
          {cliente.docs_procesados_ia > 0 && (
            <Badge variant="secondary" className="text-xs">
              {cliente.docs_procesados_ia} con IA
            </Badge>
          )}
        </div>

        {/* Último documento */}
        <div className="text-sm text-gray-600 border-t pt-3">
          <div className="flex justify-between items-center">
            <span>Último documento:</span>
            <span className="text-gray-500">{getTiempoRelativo(cliente.ultimo_documento)}</span>
          </div>
          {cliente.ultimo_estado && (
            <div className="flex justify-between items-center mt-1">
              <span>Estado:</span>
              <Badge
                variant="outline"
                className={cn(
                  'text-xs',
                  cliente.ultimo_estado === 'procesado_ia' && 'border-blue-400 text-blue-600 bg-blue-50',
                  cliente.ultimo_estado === 'pendiente' && 'border-amber-400 text-amber-600 bg-amber-50'
                )}
              >
                {cliente.ultimo_estado === 'procesado_ia' && 'En revisión'}
                {cliente.ultimo_estado === 'pendiente' && 'Pendiente'}
                {cliente.ultimo_estado === 'contabilizado' && 'Contabilizado'}
              </Badge>
            </div>
          )}
        </div>

        {/* Botón Trabajar */}
        <Button
          onClick={() => onTrabajar(cliente.id)}
          className={cn(
            'w-full transition-all duration-200',
            isHovered && 'bg-blue-600 hover:bg-blue-700'
          )}
          size="lg"
        >
          <span className="material-symbols-outlined text-sm mr-2">account_balance_wallet</span>
          Trabajar
        </Button>
      </CardContent>
    </Card>
  );
}
